import { getRendererType } from 'chessboard/core/model/get-renderer';
import { Renderer } from 'chessboard/renderers/config/types';
import type { Board } from 'chessboard/model/board/types';
import { getDefaultOptions } from 'chessboard/model/board/get-default-options';
import { transformThemeToDefaultAssets } from 'chessboard/config/theme-transformation-utils';

/**
 * Determine if renderer should be set.
 */
export function shouldSetRenderer(
  newOptions: Partial<Board.Options> = {},
  oldOptions: Board.Options,
  mergedOptions: Board.Options,
  currentRenderer: string,
) {
  const newRenderer = getRendererType(mergedOptions);

  const isDifferent = newRenderer !== currentRenderer;
  const isWebgl2d = newRenderer === Renderer.Types.WebGL2D;
  const wasAnimTypeSet = 'animationType' in newOptions;
  const isNewAnimation = newOptions.animationType !== oldOptions.animationType;
  const forceReload = isWebgl2d && wasAnimTypeSet && isNewAnimation; // CV-270348

  const perspectiveUpdated =
    newOptions.themeAssets &&
    newOptions.themeAssets.config.perspective !== oldOptions.themeAssets?.config.perspective;

  // Used to make sure a new option is being passed in that affects the renderer.
  // This is needed because you can manually pass in a renderer, which may not match the board options.
  const optionsTriggerReRender =
    perspectiveUpdated ||
    Object.keys(newOptions).some((option) =>
      Renderer.TriggerOptions.includes(<Board.SaveableOptions>option),
    );

  return optionsTriggerReRender && (isDifferent || forceReload);
}

declare global {
  interface Window {
    hasWebGLSupport: boolean;
  }
}
/**
 * Check if renderer is webgl and if it is unsupported.
 * Resets options to default if unsupported.
 */
export function checkWebGLSupport(options) {
  const newRendererType = getRendererType(options);
  const isWebGL =
    newRendererType === Renderer.Types.WebGL2D || newRendererType === Renderer.Types.WebGL3D;
  if (!isWebGL) {
    return true;
  }
  if (typeof window.hasWebGLSupport === 'undefined') {
    window.hasWebGLSupport = isWebGLSupported();
  }
  if (window.hasWebGLSupport) {
    return true;
  }

  defaultOptions(options, newRendererType === Renderer.Types.WebGL3D);

  return false;
  /**
   * Check if WebGL is supported.
   */
  function isWebGLSupported(): boolean {
    const canvas = document.createElement('canvas');
    const webglContext = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return Boolean(webglContext) && webglContext instanceof WebGLRenderingContext;
  }
}

/**
 * Reset to default options.
 * ! Performs in-place mutations on options.
 */
export function defaultOptions(options, all = false) {
  const { pieceStyle, animationType } = getDefaultOptions();
  options.animationType = animationType;
  if (all) {
    options.pieceStyle = pieceStyle;
    if (options.themeAssets) {
      options.themeAssets = transformThemeToDefaultAssets(options.themeAssets);
    }
  }
}
